import { HttpParams } from '@angular/common/http';
import { Component, inject, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { tap } from 'rxjs/operators';

import { AuthenticationDataService, AuthenticationService } from 'app/core/authentication';
import { LoginService } from 'app/core/authentication/services/login.service';
import { TranslationService } from 'app/core/internationalization/translation.service';
import { ModalService } from 'app/core/modals/services/modal.service';
import { ExploreActivitiesDataService } from 'app/explore-activities/explore-activities-data.service';
import { ExploreActivitiesOptions } from 'app/shared/components/explore-activities-block/explore-activities-options.enum';
import { StepHandlerModel } from 'app/shared/components/explore-activities-block/step-handler.model';
import { CountryCodes, RegionCodes } from 'app/shared/enums/country-code.enum';
import { ExploreActivitiesPickClassModalComponent } from 'app/shared/modals/explore-activities-pick-class-modal/explore-activities-pick-class-modal.component';
import { DemoCourseModel } from 'app/shared/models/demo-course.model';
import { DemoCoursesCountryModel } from 'app/shared/models/demo-courses-country.model';
import { DemoCoursesRegionModel } from 'app/shared/models/demo-courses-region.model';
import { ExploreActivitiesClassModel } from 'app/shared/models/explore-activities-class.model';
import { ExploreActivitiesClassInfoModel } from 'app/shared/models/explore-activities-class-info.model';
import { ExploreActivitiesUserDateModel } from 'app/shared/models/explore-activities-user-date.model';
import { LoginTokenModelWithClass } from 'app/shared/models/login-token-model-with-class';
import { TransientTeacherRegistrationModel } from 'app/shared/models/transient-teacher-registration.model';
import { TEACHER_ROUTE } from 'app/teacher/routing-constants/teacher-routing.constant';
import { TeacherRegistrationDataService } from 'app/teacher/services/teacher-registration-data.service';

@UntilDestroy()
@Component({
  selector: 'kh-explore-activities-block',
  templateUrl: './explore-activities-block.component.html',
  styleUrls: ['./explore-activities-block.component.less'],
  standalone: false
})
export class ExploreActivitiesBlockComponent implements OnInit {
  private readonly authService = inject(AuthenticationService);
  private readonly authDataService = inject(AuthenticationDataService);
  private readonly modalService = inject(ModalService);
  private readonly exploreActivitiesDataService = inject(ExploreActivitiesDataService);
  private readonly router = inject(Router);
  private readonly teacherRegistrationDataService = inject(TeacherRegistrationDataService);
  private readonly _translationService = inject(TranslationService);
  private readonly loginService = inject(LoginService);

  @Input() public countryCode?: string;
  @Input() public regionCode?: string;
  @Input() public courseId?: string;

  public localeId = this.authDataService.getUserLocale();
  public exploreActivitiesOptions = ExploreActivitiesOptions;
  public selectedOption: ExploreActivitiesOptions | null = null;
  public autoExplore = false;
  public step = 1;
  public completeStepHandlers: StepHandlerModel[] = this.getStepHandlers();
  public countries: DemoCoursesCountryModel[] = [];
  public country?: DemoCoursesCountryModel;
  public region?: DemoCoursesRegionModel | null;
  public course?: DemoCourseModel | null;
  public error: string | null = null;
  public classes: ExploreActivitiesClassInfoModel[] = [];
  public isGuest = false;
  public hasClassesToPick = false;
  public isTeacher = false;
  public teacherInfo?: ExploreActivitiesUserDateModel;

  public ngOnInit(): void {
    this.getDemoCourses();

    this.authService.userLoggedIn().pipe(
      tap(() => this.updateUserInfo()),
      untilDestroyed(this)
    ).subscribe();

    this.authService.userLoggedOut().pipe(
      tap(() => this.updateUserInfo()),
      untilDestroyed(this)
    ).subscribe();
  }

  public getDemoCourses(): void {
    this.teacherRegistrationDataService.getDemoCourses(this.localeId).pipe(
      tap((response) => {
        this.countries = response.Countries;
        this.country = this.findCountryByCode(response.CountryCode);
        this.region = this.findRegionByCode(response.RegionCode);

        if (this.countryCode) {
          this.country = this.findCountryByCode(this.countryCode);

          if (this.regionCode) {
            this.region = this.findRegionByCode(this.regionCode);

            if (this.courseId) {
              this.course = this.findCourseByCourseId(this.courseId);
              this.autoExplore = true;
            }
          }
        }

        if (!this.country) {
          this.country = this.countries[0];
          this.countrySelected(this.country);
        }

        this.updateUserInfo();
      }),
      untilDestroyed(this)
    ).subscribe();
  }

  public nextStep(): void {
    const handler = this.completeStepHandlers.find(({ Step }) => Step === this.step);

    if (handler) {
      handler.CompleteStep();
    }
  }

  public prevStep(): void {
    this.error = null;
    this.step--;
  }

  public countrySelected(country: DemoCoursesCountryModel): void {
    this.country = country;

    switch (country.CountryCode) {
      case CountryCodes.Canada:
        this.region = country.Regions.find(({ RegionCode }) => RegionCode === RegionCodes.Ontario);
        break;
      default:
        this.region = country.Regions[0];
        break;
    }
  }

  public optionSelected(option: ExploreActivitiesOptions): void {
    this.selectedOption = option;
    this.nextStep();
  }

  public showClassPicker(): void {
    this.modalService.displayModal<ExploreActivitiesPickClassModalComponent>(ExploreActivitiesPickClassModalComponent, {
      classes: this.classes,
      courseId: this.course?.CourseId ?? null
    }, 'md', true).content?.exploringStarted.pipe(
      tap((event) => {
        this.goToCurriculumPage(event.classId, event.courseId)
          .catch((error: unknown) => console.error(error));
      }),
      untilDestroyed(this)
    ).subscribe();
  }

  public getNameForDropdown(item: DemoCoursesCountryModel | DemoCoursesRegionModel | DemoCourseModel): string | undefined {
    return item?.Name;
  }

  private getStepHandlers(): StepHandlerModel[] {
    return [
      {
        Step: 1,
        CompleteStep: () => {
          if (!this.hasClassesToPick) {
            this.startExploring();
          }
          else {
            this.step++;
          }
        }
      },
      {
        Step: 2,
        CompleteStep: () => {
          if (this.selectedOption === ExploreActivitiesOptions.ContinueWithExistingClass) {
            this.showClassPicker();
          }
          else {
            this.createNewClass();
          }
        }
      },
      {
        Step: 3,
        CompleteStep: () => this.createNewClass()
      }
    ];
  }

  private startExploring(): void {
    if (!this.ensureCourseSelected()) {
      return;
    }

    if (this.isGuest) {
      const model: TransientTeacherRegistrationModel = {
        CourseId: this.course?.CourseId,
        LocaleId: this.localeId
      };

      this.teacherRegistrationDataService.registerForFreeGameShow(model).pipe(
        tap((token) => this.saveTokenAndNavigate(token)),
        untilDestroyed(this)
      ).subscribe();
    }
    else {
      this.createClass();
    }
  }

  private createNewClass(): void {
    if (!this.ensureCourseSelected()) {
      this.step = 3;

      return;
    }

    this.createClass();
  }

  private createClass(): void {
    const model: ExploreActivitiesClassModel = {
      CourseId: this.course!.CourseId,
      CountryCode: this.country!.CountryCode,
      RegionCode: this.region!.RegionCode
    };

    this.exploreActivitiesDataService.createExploreActivitiesClass(model).pipe(
      tap((tokenData) => {
        this.saveTokenAndNavigate(tokenData).catch((error: unknown) => console.error(error));
      }),
      untilDestroyed(this)
    ).subscribe();
  }

  private async saveTokenAndNavigate(tokenData: LoginTokenModelWithClass): Promise<void> {
    this.loginService.setTokenResponse(tokenData);

    await this.goToCurriculumPage(tokenData.ClassId);
  }

  private updateUserInfo(): void {
    this.step = 1;
    this.isGuest = !this.authService.isUserAuthorized() || this.authService.isTransient();
    this.isTeacher = this.authService.isInRole('Teacher');
    this.hasClassesToPick = false;
    this.selectedOption = null;

    if (!this.isGuest && this.isTeacher) {
      this.exploreActivitiesDataService.getUserData().pipe(
        tap((teacherInfo) => {
          this.teacherInfo = teacherInfo;
          this.hasClassesToPick = teacherInfo.Classes.length > 0;

          this.classes = teacherInfo.Classes;
          this.country = this.findCountryByCode(teacherInfo.CountryCode);
          this.region = this.findRegionByCode(teacherInfo.RegionCode);

          if (this.autoExplore) {
            this.autoExplore = false;
            this.nextStep();
          }
        }),
        untilDestroyed(this)
      ).subscribe();
    }
    else {
      if (this.autoExplore) {
        this.autoExplore = false;
        this.nextStep();
      }
    }
  }

  private findCountryByCode(countryCode: string): DemoCoursesCountryModel | undefined {
    return this.countries.find(({ CountryCode }) => CountryCode === countryCode);
  }

  private findRegionByCode(regionCode: string): DemoCoursesRegionModel | undefined | null {
    return this.country ? this.country.Regions.find(({ RegionCode }) => RegionCode === regionCode) : null;
  }

  private findCourseByCourseId(courseId: string): DemoCourseModel | undefined | null {
    return (this.region) ? this.region.Courses.find(({ CourseId }) => CourseId === courseId) : null;
  }

  private ensureCourseSelected(): boolean {
    this.error = null;

    if (!this.course) {
      this.error = this._translationService.instant('EXPLORE_ACTIVITIES_BLOCK.PLEASE_SELECT_A_COURSE');
    }

    return this.error === null;
  }

  private async goToCurriculumPage(classId: string, courseId = ''): Promise<void> {
    const route = TEACHER_ROUTE.ACTIVITIES.CURRICULUM.route(classId);
    const queryParams = new HttpParams()
      .set('courseid', courseId || (this.course?.CourseId ?? ''))
      .set('countrycode', this.country?.CountryCode ?? '')
      .set('regionCode', this.region?.RegionCode ?? '');

    await this.router.navigate(route, { queryParams });
  }
}
